import {Injectable, WritableSignal} from '@angular/core';
import {WritableSignalMap} from '../util/writable-signal-map';
import {StringMap} from '../util/interfaces';

const PREFS_KEY = 'dom-user-prefs';
const OLD_TRUST_TOKEN_KEY = 'dom-trust-token';

interface PrefsStore {
    lastEmail?: string;
    users: { [email: string]: { [key: string]: any } };
}

/** Persistent per-user preferences that survive logout.
 *  Maps each user email to a key/value map. Only the current user's prefs are loaded as reactive signals.
 */
@Injectable({
    providedIn: 'root'
})
export class UserPrefsService {

    private currentEmail?: string;
    private signals = new WritableSignalMap();

    /** The email of the currently loaded user, if any. */
    get loadedEmail(): string | undefined {
        return this.currentEmail;
    }

    constructor() {
        this.migrateOldTrustTokens();
        const store = this.readStore();
        if (store.lastEmail)
            this.loadFromStore(store, store.lastEmail);
    }

    /** Load a user's prefs into memory as reactive signals. Also updates lastEmail. */
    load(email: string) {
        const store = this.readStore();
        store.lastEmail = email;
        this.writeStore(store);
        this.loadFromStore(store, email);
    }

    /** Get a reactive signal for a pref key (current loaded user). */
    get<T = any>(key: string): WritableSignal<T | undefined> {
        return this.signals.get(key);
    }

    /** Set a pref for the current loaded user and persist. */
    set<T = any>(key: string, value: T) {
        if (!this.currentEmail)
            return;
        this.signals.set(key, value);
        const store = this.readStore();
        if (!store.users[this.currentEmail])
            store.users[this.currentEmail] = {};
        store.users[this.currentEmail][key] = value;
        this.writeStore(store);
    }

    /** Remove a pref for the current loaded user and persist. */
    remove(key: string) {
        if (!this.currentEmail)
            return;
        this.signals.delete(key);
        const store = this.readStore();
        if (store.users[this.currentEmail]) {
            delete store.users[this.currentEmail][key];
            this.writeStore(store);
        }
    }

    /** Read a pref for any user directly from localStorage without loading into memory. */
    peek<T = any>(email: string, key: string): T | undefined {
        const store = this.readStore();
        return store.users[email]?.[key] as T | undefined;
    }

    /** Set a pref for a specific user directly in localStorage (without loading them). */
    setFor<T = any>(email: string, key: string, value: T) {
        const store = this.readStore();
        if (!store.users[email])
            store.users[email] = {};
        store.users[email][key] = value;
        this.writeStore(store);
    }

    /** Remove a pref for a specific user directly in localStorage (without loading them). */
    removeFor(email: string, key: string) {
        const store = this.readStore();
        if (store.users[email]) {
            delete store.users[email][key];
            this.writeStore(store);
        }
    }

    /** Clear in-memory signals (e.g. on logout). localStorage is NOT touched. */
    unload() {
        this.currentEmail = undefined;
        this.signals.clear();
    }

    private loadFromStore(store: PrefsStore, email: string) {
        this.currentEmail = email;
        this.signals.clear();
        const userPrefs = store.users[email];
        if (userPrefs) {
            Object.keys(userPrefs).forEach((key) => {
                this.signals.set(key, userPrefs[key]);
            });
        }
    }

    private readStore(): PrefsStore {
        const raw = localStorage.getItem(PREFS_KEY);
        if (!raw)
            return {users: {}};
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.users)
                return parsed;
            return {users: {}};
        } catch {
            return {users: {}};
        }
    }

    private writeStore(store: PrefsStore) {
        localStorage.setItem(PREFS_KEY, JSON.stringify(store));
    }

    /** One-time migration: move old trust tokens into the new prefs structure. */
    private migrateOldTrustTokens() {
        const raw = localStorage.getItem(OLD_TRUST_TOKEN_KEY);
        if (!raw)
            return;
        try {
            const tokens: StringMap = JSON.parse(raw);
            if (tokens && typeof tokens === 'object' && tokens.constructor.name === 'Object') {
                const store = this.readStore();
                Object.keys(tokens).forEach((email) => {
                    if (!store.users[email])
                        store.users[email] = {};
                    store.users[email]['trustToken'] = tokens[email];
                });
                this.writeStore(store);
            }
        } catch { /* ignore malformed data */ }
        localStorage.removeItem(OLD_TRUST_TOKEN_KEY);
    }
}
